/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { type Client } from '../../lib/api/resources.js';

interface ChildSelectProps {
  readonly clients: readonly Client[];
  readonly value: string;
  readonly onSelect: (id: string) => void;
}

/** Child picker for parents with more than one enrolled child. */
export function ChildSelect({ clients, value, onSelect }: ChildSelectProps): JSX.Element | null {
  const { t } = useTranslation();
  const handle = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      onSelect(event.target.value);
    },
    [onSelect],
  );

  if (clients.length <= 1) {
    return null;
  }
  return (
    <label className="mb-6 flex items-center gap-2 text-sm text-slate-600">
      <span>{t('parent.selectChild')}</span>
      <select
        className="focus-ring rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
        value={value}
        onChange={handle}
      >
        {clients.map((child) => (
          <option key={child.id} value={child.id}>
            {child.fullName}
          </option>
        ))}
      </select>
    </label>
  );
}
